import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { MessageSquareText, Users, FileText, Wallet, ShieldCheck, Tag, BarChart3, LogOut, Languages, Sparkles, Send, ChevronDown, ChevronLeft, ChevronRight, LayoutTemplate, Receipt, Building2, Calculator, Landmark } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useI18n } from "../../i18n";
import { api } from "../../lib/api";

const navItemBase = "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-bold uppercase transition-colors";

export default function Sidebar({ onClose, collapsed = false, onToggleCollapsed }) {
    const { user, logout } = useAuth();
    const { t, lang, toggleLang } = useI18n();
    const navigate = useNavigate();
    const isAdmin = user?.role === "super_admin";
    const [modulesOpen, setModulesOpen] = useState(true);
    const [pivaAdeOpen, setPivaAdeOpen] = useState(false);
    const [declarationsOpen, setDeclarationsOpen] = useState(true);
    const [declarationModulesOpen, setDeclarationModulesOpen] = useState({});
    const [accountOpen, setAccountOpen] = useState(false);
    const [paymentsOpen, setPaymentsOpen] = useState(false);
    const [paymentCreditsOpen, setPaymentCreditsOpen] = useState(false);
    const [catalog, setCatalog] = useState([]);

    useEffect(() => {
        let active = true;
        api.get("/pratiche/modules")
            .then((r) => { if (active) setCatalog(r.data || []); })
            .catch(() => { if (active) setCatalog([]); });
        return () => { active = false; };
    }, []);

    const enabledProducts = catalog.filter((m) => m.enabled && m.status === "active");
    const pivaEnabled = isAdmin || enabledProducts.some((m) => m.code === "PIVA_ADE");
    const declarations = enabledProducts.filter((m) => m.category === "dichiarativi_ade");

    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    if (collapsed) {
        return (
            <aside
                data-testid="app-sidebar"
                data-collapsed="true"
                className="w-0 shrink-0 h-screen sticky top-0 z-30 overflow-visible"
            >
                <button
                    type="button"
                    data-testid="sidebar-expand-toggle"
                    onClick={onToggleCollapsed}
                    aria-label="Mostra menu laterale"
                    title="Mostra menu laterale"
                    className="absolute top-5 left-0 w-8 h-12 rounded-r-lg border border-l-0 border-border bg-primary text-white shadow-lg flex items-center justify-center hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400"
                >
                    <ChevronRight size={20} />
                </button>
            </aside>
        );
    }

    return (
        <aside data-testid="app-sidebar" data-collapsed="false" className="relative w-72 shrink-0 h-screen sticky top-0 border-e border-border bg-background-alt/70 backdrop-blur-sm flex flex-col transition-[width] duration-200">
            <button
                type="button"
                data-testid="sidebar-collapse-toggle"
                onClick={onToggleCollapsed}
                aria-label="Nascondi menu laterale"
                title="Nascondi menu laterale"
                className="absolute z-40 top-5 -right-4 w-8 h-12 rounded-lg border border-border bg-primary text-white shadow-lg flex items-center justify-center hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400"
            >
                <ChevronLeft size={20} />
            </button>
            {/* Brand */}
            <div className="px-5 pt-6 pb-4 border-b border-border">
                <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-base">F</div>
                    <div>
                        <div className="font-display text-lg font-semibold leading-tight">FiscoAI</div>
                        <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{t.app.tagline}</div>
                    </div>
                </div>
            </div>

            {/* User card */}
            <div className="px-4 py-3 mt-2">
                <div className="rounded-md bg-card border border-border px-3 py-2.5">
                    <div className="text-sm font-medium text-foreground truncate">{user?.full_name || user?.email}</div>
                    <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                    {!isAdmin && (
                        <div data-testid="sidebar-credits-balance" className="mt-2 flex items-center gap-1.5 text-xs">
                            <Sparkles size={12} className="text-primary" />
                            <span className="text-muted-foreground">{t.common.creditsLeft}:</span>
                            <span className="font-semibold text-foreground">{Number(user?.credits_balance || 0).toFixed(2)}</span>
                        </div>
                    )}
                    {isAdmin && (
                        <div className="mt-1 text-[10px] uppercase tracking-wider font-semibold text-primary">Super Admin</div>
                    )}
                </div>
            </div>

            {/* Nav */}
            <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
                <NavItem to="/chat" testid="nav-chat" icon={<MessageSquareText size={16} />} label={t.nav.chat} onClick={onClose} />
                <NavItem to="/clients" testid="nav-clients" icon={<Users size={16} />} label={t.nav.clients} onClick={onClose} />
                <div data-testid="nav-moduli-tree" className="pt-1">
                    <button
                        type="button"
                        data-testid="nav-moduli-toggle"
                        onClick={() => setModulesOpen((v) => !v)}
                        className={`${navItemBase} w-full justify-between bg-primary text-white tracking-wide hover:bg-orange-600`}
                    >
                        <span className="flex items-center gap-3">
                            <FileText size={16} className="text-white" />
                            <span>{t.nav.pratiche}</span>
                        </span>
                        {modulesOpen ? <ChevronDown size={15} className="text-white" /> : <ChevronRight size={15} className="text-white" />}
                    </button>
                    {modulesOpen && (
                        <div className="ms-2 border-l-2 border-orange-300 ps-2 space-y-0.5 pt-1">
                            {pivaEnabled && <div data-testid="nav-piva-ade-tree" className="rounded-md overflow-hidden">
                                <button
                                    type="button"
                                    data-testid="nav-piva-ade-toggle"
                                    onClick={() => setPivaAdeOpen((v) => !v)}
                                    className={`${navItemBase} w-full justify-between bg-primary/90 text-white text-xs hover:bg-orange-600`}
                                >
                                    <span className="flex items-center gap-3">
                                        <FileText size={14} className="text-white" />
                                        <span>{t.nav.pivaAde}</span>
                                    </span>
                                    {pivaAdeOpen ? <ChevronDown size={14} className="text-white" /> : <ChevronRight size={14} className="text-white" />}
                                </button>
                                {pivaAdeOpen && (
                                    <div className="ms-4 border-l-2 border-white/80 ps-2 pb-1 space-y-0.5 bg-primary/90">
                                        <NavItem to="/pratiche" testid="nav-piva-ade-to-send" icon={<FileText size={12} />} label={t.nav.toSend} onClick={onClose} nested small />
                                        <NavItem to="/pratiche?tab=sent" testid="nav-piva-ade-sent" icon={<Send size={12} />} label={t.nav.sent} onClick={onClose} nested small />
                                        <NavItem to="/pratiche?tab=templates" testid="nav-piva-ade-templates" icon={<LayoutTemplate size={12} />} label={t.nav.templates} onClick={onClose} nested small />
                                    </div>
                                )}
                            </div>}
                            {declarations.length > 0 && (
                                <div data-testid="nav-declarations-tree" className="rounded-md overflow-hidden">
                                    <button
                                        type="button"
                                        data-testid="nav-declarations-toggle"
                                        onClick={() => setDeclarationsOpen((v) => !v)}
                                        className={`${navItemBase} w-full justify-between bg-primary/90 text-white text-xs hover:bg-orange-600`}
                                    >
                                        <span className="flex items-center gap-3">
                                            <FileText size={14} className="text-white" />
                                            <span>Dichiarativi</span>
                                        </span>
                                        {declarationsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                    </button>
                                    {declarationsOpen && (
                                        <div className="ms-3 border-l-2 border-white/80 ps-2 pb-1 space-y-1 bg-primary/90">
                                            {declarations.map((module) => {
                                                const open = Boolean(declarationModulesOpen[module.code]);
                                                return (
                                                    <div key={module.code} data-testid={`nav-declaration-${module.code.toLowerCase()}`}>
                                                        <button
                                                            type="button"
                                                            onClick={() => setDeclarationModulesOpen((current) => ({ ...current, [module.code]: !open }))}
                                                            className={`${navItemBase} w-full justify-between bg-primary/80 text-white text-[11px] hover:bg-orange-600`}
                                                        >
                                                            <span className="flex items-center gap-2 min-w-0">
                                                                <ModuleIcon name={module.icon} />
                                                                <span className="truncate">{module.name}</span>
                                                            </span>
                                                            {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                                        </button>
                                                        {open && (
                                                            <div className="ms-4 border-l border-white/70 ps-2 space-y-0.5">
                                                                <NavItem to={`/moduli/${module.code}?tab=to_send`} testid={`nav-module-${module.code.toLowerCase()}-to-send`} icon={<FileText size={11} />} label={t.nav.toSend} onClick={onClose} nested small />
                                                                <NavItem to={`/moduli/${module.code}?tab=sent`} testid={`nav-module-${module.code.toLowerCase()}-sent`} icon={<Send size={11} />} label={t.nav.sent} onClick={onClose} nested small />
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                            {!isAdmin && catalog.length > 0 && declarations.length === 0 && (
                                <div className="px-3 py-2 text-[11px] text-muted-foreground">Nessun dichiarativo abilitato</div>
                            )}
                        </div>
                    )}
                </div>
                <div data-testid="nav-account-tree" className="pt-1">
                    <button
                        type="button"
                        data-testid="nav-account-toggle"
                        onClick={() => setAccountOpen((v) => !v)}
                        className={`${navItemBase} w-full justify-between bg-primary text-white tracking-wide hover:bg-orange-600`}
                    >
                        <span className="flex items-center gap-3">
                            <Users size={16} className="text-white" />
                            <span>{t.nav.account}</span>
                        </span>
                        {accountOpen ? <ChevronDown size={15} className="text-white" /> : <ChevronRight size={15} className="text-white" />}
                    </button>
                    {accountOpen && (
                        <div className="ms-2 border-l-2 border-orange-300 ps-2 space-y-0.5 pt-1">
                            <button type="button" data-testid="nav-account-profile" className={`${navItemBase} w-full text-xs bg-primary/80 text-white cursor-default`} title="Sezione anagrafica utente futura">
                                <Users size={14} className="text-white" />
                                <span>{t.nav.userProfile}</span>
                            </button>
                            <NavItem to="/telematico?tab=settings" testid="nav-account-entratel" icon={<Send size={14} />} label={t.nav.entratel} onClick={onClose} nested />
                            <button type="button" data-testid="nav-account-telemaco" className={`${navItemBase} w-full text-xs bg-primary/80 text-white cursor-default`} title="Modulo futuro">
                                <FileText size={14} className="text-white" />
                                <span>{t.nav.telemaco}</span>
                            </button>
                        </div>
                    )}
                </div>
                {!isAdmin && (
                    <NavItem to="/credits" testid="nav-credits" icon={<Wallet size={16} />} label={t.nav.credits} onClick={onClose} />
                )}

                {isAdmin && (
                    <div data-testid="nav-payments-tree" className="pt-1">
                        <button
                            type="button"
                            data-testid="nav-payments-toggle"
                            onClick={() => setPaymentsOpen((v) => !v)}
                            className={`${navItemBase} w-full justify-between bg-primary text-white tracking-wide hover:bg-orange-600`}
                        >
                            <span className="flex items-center gap-3">
                                <Wallet size={16} className="text-white" />
                                <span>{t.nav.payments}</span>
                            </span>
                            {paymentsOpen ? <ChevronDown size={15} className="text-white" /> : <ChevronRight size={15} className="text-white" />}
                        </button>
                        {paymentsOpen && (
                            <div className="ms-2 border-l-2 border-orange-300 ps-2 space-y-0.5 pt-1">
                                <NavItem to="/admin/payments/provider" testid="nav-payments-provider" icon={<Wallet size={14} />} label={t.nav.paymentProvider} onClick={onClose} nested />
                                <NavItem to="/admin/payments/accrediti" testid="nav-payments-accrediti" icon={<Wallet size={14} />} label={t.nav.affiliateCredits} onClick={onClose} nested />
                                <div data-testid="nav-payment-credits-tree" className="rounded-md overflow-hidden">
                                    <button
                                        type="button"
                                        data-testid="nav-payment-credits-toggle"
                                        onClick={() => setPaymentCreditsOpen((v) => !v)}
                                        className={`${navItemBase} w-full justify-between bg-primary/90 text-white text-xs hover:bg-orange-600`}
                                    >
                                        <span className="flex items-center gap-3">
                                            <Tag size={14} className="text-white" />
                                            <span>{t.nav.paymentCredits}</span>
                                        </span>
                                        {paymentCreditsOpen ? <ChevronDown size={14} className="text-white" /> : <ChevronRight size={14} className="text-white" />}
                                    </button>
                                    {paymentCreditsOpen && (
                                        <div className="ms-4 border-l-2 border-white/80 ps-2 pb-1 space-y-0.5 bg-primary/90">
                                            <NavItem to="/admin/payments/crediti-generali" testid="nav-payment-credit-general" icon={<Tag size={12} />} label={t.nav.creditGeneralSettings} onClick={onClose} nested small />
                                            <NavItem to="/admin/payments/tariffario" testid="nav-payment-tariffario" icon={<Tag size={12} />} label={t.nav.tariffario} onClick={onClose} nested small />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {isAdmin && (
                    <>
                        <div className="mt-4 mb-1.5 px-3 text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">{t.nav.admin}</div>
                        <NavItem to="/admin/stats" testid="nav-admin-stats" icon={<BarChart3 size={16} />} label={t.nav.stats} onClick={onClose} />
                        <NavItem to="/admin/affiliates" testid="nav-admin-affiliates" icon={<ShieldCheck size={16} />} label={t.nav.affiliates} onClick={onClose} />
                    </>
                )}
            </nav>

            {/* Footer */}
            <div className="px-3 py-3 border-t border-border space-y-1">
                <button
                    data-testid="language-switcher"
                    onClick={toggleLang}
                    className={`${navItemBase} w-full justify-between bg-primary text-white hover:bg-orange-600`}
                >
                    <span className="flex items-center gap-3"><Languages size={16} /> {lang === "it" ? "Lingua" : "اللغة"}</span>
                    <span className="text-xs font-mono uppercase font-bold text-white">{lang === "it" ? "IT → AR" : "AR → IT"}</span>
                </button>
                <button
                    data-testid="logout-button"
                    onClick={handleLogout}
                    className={`${navItemBase} w-full bg-primary text-white hover:bg-orange-600`}
                >
                    <LogOut size={16} />{t.nav.logout}
                </button>
            </div>
        </aside>
    );
}

function ModuleIcon({ name }) {
    const icons = { Receipt, Building2, Users, Calculator, Landmark, FileText };
    const Icon = icons[name] || FileText;
    return <Icon size={14} />;
}

function NavItem({ to, icon, label, onClick, testid, nested = false, small = false }) {
    return (
        <NavLink
            to={to}
            data-testid={testid}
            onClick={onClick}
            className={({ isActive }) => `${navItemBase} ${nested ? "text-xs" : ""} ${small ? "py-1.5 ps-2" : ""} ${isActive ? "bg-orange-600 text-white" : "bg-primary text-white hover:bg-orange-600"}`}
        >
            {icon}<span>{label}</span>
        </NavLink>
    );
}
